import { Document } from 'schemas/DocumentSchema'
import { MessageConst } from 'enums'
import AuthorSex from 'enums/AuthorSex'
import { SoundbiteSentimentStrings } from 'enums/SoundbiteSentiment'
import { StreamSymbolStrings } from 'enums/StreamSymbol'
import VerifiedUsers from 'enums/VerifiedUsers'
import { TimeZoneStrings } from 'enums/TimeZone'
import toXDotComUrl from 'utils/twitterUrlUtil'
import { Metadata } from './metadata'

export class DocumentMetadataAdaptor implements Metadata {
  constructor(
    private document: Document,
    private timeZone: TimeZoneStrings,
    private annotatedSentiment?: SoundbiteSentimentStrings,
    private vocDisplay?: boolean,
    private needTruncated?: boolean,
    private truncatedLength?: number
  ) {}

  getTimeZone = () => this.timeZone

  isVocDisplay = () => this.vocDisplay

  isNeedTruncated = () => this.needTruncated

  getTruncatedLength = () => this.truncatedLength

  getWidgetType = () => undefined

  getAuthor = () => this.document.properties.author

  getAuthorId = () => this.document.properties.authorID

  getAuthorKarma = () =>
    this.document.properties.authorKarma ? this.document.properties.authorKarma : undefined

  getAuthorLink = () => this.document.properties.authorLink

  getAuthorLocation = () => this.document.properties.authorLocation

  getAuthorName = () => this.document.properties.authorName

  getAuthorUrl = () => this.document.properties.authorLink

  getAuthorHandle = () => this.document.authorHandle

  getAvatarUrl = () => this.document.properties.authorAvatarUrl

  getOriginalAuthorAvatarUrl = () => this.document.properties.originalAuthorAvatarUrl

  getOriginalAuthorHandle = () => this.document.originalAuthorHandle

  getDateTime = () =>
    this.document.properties.datetime ? this.document.properties.datetime : undefined

  getDisplayTextRange = () => this.document.displayTextRange

  getDocumentId = () => this.document.documentId

  getDomain = () => this.document.properties.domain

  getHighlightingInfos = () => this.document.sentenceHighlightInfos

  isDocumentMode = () => true

  getOriginalAuthor = () => this.document.properties.originalAuthor

  getOriginalAuthorName = () => this.document.properties.originalAuthorName

  getOriginalAuthorUrl = () => this.document.properties.originalAuthorUrl

  getPostType = () => this.document.properties.postType

  getPostId = () => this.document.properties.postId

  getSentences = () => this.document.sentences as string[]

  getSentenceInfos = () => this.document.sentenceInfos

  getSoundbiteSentiment = () => this.document.sentiment

  // NI-2659: show x.com links so readers are not forced through the X login wall.
  getUrl = () => toXDotComUrl(this.document.properties.url)

  getFavicon = () => undefined

  getMentionedUrls = () => this.document.mentionedUrls

  getAuthorFollower = () => this.document.properties.authorFollower

  getAuthorGender = () =>
    this.document.properties.authorSex ? this.document.properties.authorSex : AuthorSex.UNKNOWN

  getAuthorGeoInfos = () => this.document.properties.authorGeoHigh

  isTweet = () => this.document.properties.isTweet!

  getDataStreams = () => this.document.properties.dataStreams as StreamSymbolStrings[] | undefined

  getAnnotatedSentiment = () => this.annotatedSentiment

  isDeleted = () => this.document.deleted

  isSimilar = () => MessageConst.NO

  getDocumentMetaTags = () => this.document.metaTags as string[]

  getSentenceAllTags = () => this.document.sentenceAllTags as string[][]

  getSentenceUserTags = () => this.document.sentenceUserTags as string[][]

  getSentenceMetaTags = () => this.document.sentenceMetaTags as string[][]

  getUserTags = () => undefined

  getMetaTags = () => undefined

  getTitle = () => this.document.properties.title

  isAuthorVerified = () => this.document.properties.authorAccountStatus === VerifiedUsers.VERIFIED

  isOriginalAuthorVerified = () =>
    this.document.properties.originalAuthorAccountStatus === VerifiedUsers.VERIFIED

  getSourceType = () => this.document.properties.sourceType

  getSourceName = () => this.document.sourceName

  getHitSentence = () => this.document.hitSentence

  getProfessions = () => this.document.properties.professions as string[]

  getInterests = () => this.document.properties.interests as string[]

  getEngagement = () =>
    this.document.properties.postEngagements !== undefined
      ? Number(this.document.properties.postEngagements)
      : ''

  getReplies = () =>
    this.document.properties.comments !== undefined ? Number(this.document.properties.comments) : ''

  getComments = () => undefined

  getImpressions = () => undefined

  getShares = () => undefined

  getNegSentiment = () => undefined

  getPassion = () => undefined

  getPosSentiment = () => undefined

  getReblogs = () => undefined

  getSentiments = () => undefined

  getRetweets = () =>
    this.document.properties.shares !== undefined ? Number(this.document.properties.shares) : ''

  getLikes = () =>
    this.document.properties.likes !== undefined ? Number(this.document.properties.likes) : ''

  getViews = () =>
    this.document.properties.views !== undefined ? Number(this.document.properties.views) : ''

  getScore = () => (this.document.properties.scores ? this.document.properties.scores : undefined)

  getProductName = () => this.document.properties.productName

  getDocHierarchy = () => this.document.properties.docHierarchy

  getPostRating = () => this.document.properties.postRating

  getPhotoUserTags = () => this.document.photoUserTags as string[]

  getAdsType = () => this.document.properties.adsType

  getPersistId = () => this.document.properties.persistId

  getOverallStarRating = () => this.document.properties.overallStarRating

  getOverallSurveyScore = () => this.document.properties.overallSurveyScore

  getNetPromoterScore = () => this.document.properties.netPromoterScore

  getMeasurements = () => this.document.measurements

  getSentenceMeasurements = () => this.document.sentenceMeasurements

  getParentDistilledTexts = () => this.document.properties.parentDistilledTexts as string[]

  getParentDisplayTextRange = () => this.document.properties.parentDisplayTextRange

  getParentMentionedUrls = () => this.document.properties.parentMentionedUrls

  getFingerprint = () => undefined

  getSentenceNumber = () => undefined
}

export default DocumentMetadataAdaptor
